import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import {
  Save,
  Edit2,
  X,
  ChevronRight,
  ChevronLeft,
  Loader2,
  Filter,
} from "lucide-react";
import ErrorNotification from "../ErrorNotification";
import UnauthorizedPage from "../UnauthorizedPage";
const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [showFilter, setShowFilter] = useState(false);
  const [filterValue, setFilterValue] = useState("");
  const [itemsPerPage] = useState(5);
  const [isEditing, setIsEditing] = useState(false);
  const [pages, setPages] = useState([]);
  const [tempPermissions, setTempPermissions] = useState({});
  const [showAllPages, setShowAllPages] = useState(false);
  const [visiblePages, setVisiblePages] = useState([
    "endpointPage",
    "globalConfigPage",
    "userManagementPage",
  ]);
  const accessToken = useSelector((state) => state.accessToken.value);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [permissions, sethandlePermissions] = useState(true);
  const filterRef = useRef(null);
  const apiUrl = process.env.REACT_APP_API_URL;
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);
  useEffect(() => {
    getData();
  }, []);
  useEffect(() => {
    function handleClickOutside(event) {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setShowFilter(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape") {
        setShowFilter(false);
      }
    });

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleClickOutside);
    };
  }, []);
  const filteredUsers = users.filter((user) =>
    user.userEmail.toLowerCase().includes(filterValue.toLowerCase())
  );

  const pageActions = {
    View: "READ",
    Edit: "WRITE",
    Delete: "DELETE",
  };

  const indexOfLastUser = currentPage * itemsPerPage;
  const indexOfFirstUser = indexOfLastUser - itemsPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);

  const getData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.post(
        apiUrl,
        {
          query: `
            query MyQuery {
              getUsers {
                companies
                pages {
                  endpointPage
                  globalConfigPage
                  userManagementPage
                  securityPage
                  resourceSchedulerPage
                  costManagementPage
                  performancePage
                }
                userEmail
                userId
                userRole
              }
            }
          `,
        },
        { headers: { Authorization: accessToken } }
      );

      if (
        !response.data?.data?.getUsers ||
        !Array.isArray(response.data.data.getUsers)
      ) {
        throw new Error(
          "You do not have permission to view the User Management page"
        );
      }

      setUsers(response.data.data.getUsers);

      if (response.data.data.getUsers[0]?.pages) {
        const pageNames = Object.keys(response.data.data.getUsers[0].pages);
        setPages(
          pageNames.map((pageName) => ({
            id: pageName,
            name: formatPageName(pageName),
          }))
        );
        setVisiblePages(pageNames.slice(0, 3));
      }
    } catch (error) {
      sethandlePermissions(false);
    } finally {
      setLoading(false);
    }
  };

  const formatPageName = (pageName) => {
    const name = pageName.replace(/Page$/, "");
    const words = name.split(/(?=[A-Z])/);
    return words
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const handlePermissionChange = (userId, page, level) => {
    setTempPermissions((prev) => ({
      ...prev,
      [userId]: {
        ...prev[userId],
        [page]: level,
      },
    }));
  };
  const getPageNumbers = () => {
    const maxPagesToShow = 5;
    let pageNumbers = [];

    if (totalPages <= maxPagesToShow) {
      pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);
    } else {
      pageNumbers.push(1);

      if (currentPage <= 3) {
        pageNumbers.push(2, 3, 4);
        pageNumbers.push('...');
        pageNumbers.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pageNumbers.push('...');
        pageNumbers.push(totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
      } else {
        pageNumbers.push('...');
        pageNumbers.push(currentPage - 1, currentPage, currentPage + 1);
        pageNumbers.push('...');
        pageNumbers.push(totalPages);
      }
    }

    return pageNumbers;
  };
  const saveChanges = async () => {
    setIsSaving(true);
    setError(null);
    const updatePromises = [];

    // Only update users that actually have changes
    for (const userId in tempPermissions) {
      const userChanges = tempPermissions[userId];
      const originalUser = users.find(user => user.userId === userId);

      // Check if there are actual changes for this user
      const hasChanges = Object.keys(userChanges).some(page =>
        userChanges[page] !== originalUser.pages[page]
      );

      if (hasChanges) {
        // Only send the pages that actually changed
        const changedPages = {};
        Object.keys(userChanges).forEach(page => {
          if (userChanges[page] !== originalUser.pages[page]) {
            changedPages[page] = userChanges[page];
          }
        });

        // Make single API call per user with only changed pages
        updatePromises.push(updateUserPermissionBatch(userId, changedPages));
      }
    }

    try {
      await Promise.all(updatePromises);
      setTempPermissions({});
      setIsEditing(false);
    } catch (error) {
      setError(error.message || "Failed to update user permissions");
    } finally {
      setIsSaving(false);
    }
  };
  const updateUserPermissionBatch = async (userId, changedPages) => {
    setUpdating(userId);

    try {
      const variableDefinitions = Object.keys(changedPages)
        .map((page) => `$${page}Value: Int`)
        .join(", ");

      const mutationFields = Object.keys(changedPages)
        .map((page) => `${page}Value: $${page}Value`)
        .join("\n        ");

      const returnFields = pages.map((p) => p.id).join("\n              ");

      const mutation = `
      mutation UpdateUserPages(
        $userId: ID!,
        ${variableDefinitions}
      ) {
        updateUserPages(
          userId: $userId,
          ${mutationFields}
        ) {
          userId
          pages {
            ${returnFields}
          }
        }
      }
    `;

      const variables = { userId };
      Object.keys(changedPages).forEach(page => {
        variables[`${page}Value`] = changedPages[page];
      });
      console.log(mutation)

      const response = await axios.post(
        apiUrl,
        {
          query: mutation,
          variables,
        },
        { headers: { Authorization: accessToken } }
      );
      console.log(response.data)

      if (response.data?.data?.updateUserPages) {
        const updatedUser = response.data.data.updateUserPages;
        setUsers((prevUsers) =>
          prevUsers.map((user) =>
            user.userId === userId
              ? { ...user, pages: updatedUser.pages }
              : user
          )
        );
      } else {
        throw new Error(
          "You do not have permission to edit User Management page"
        );
      }
    } catch (error) {
      throw new Error(error.message || "Failed to update user permissions");
    } finally {
      setUpdating(null);
    }
  };

  const PermissionCheck = ({ isActive, onClick, isUpdating }) => (
    <div
      className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all duration-200 ease-in-out
        ${isUpdating || isSaving ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
        ${isActive ? "bg-[#003366] border-[#003366] hover:bg-[#004488]" : "bg-white border-gray-300 hover:border-gray-400"}`}
      onClick={!(isUpdating || isSaving) ? onClick : undefined}
    >
      {isActive && <div className="w-2 h-2 bg-white rounded-full" />}
    </div>
  );

  const toggleEditMode = () => {
    if (!isEditing) {
      const initialPermissions = {};
      users.forEach((user) => {
        initialPermissions[user.userId] = user.pages;
      });
      setTempPermissions(initialPermissions);
    }
    setIsEditing(!isEditing);
  };
  const getUserRole = (userPages) => {
    if (!userPages || typeof userPages !== "object") return "Member";
    const isAdmin = pages.every((page) => userPages[page.id] === 3);
    return isAdmin ? "admin" : "member";
  };
  const handleViewMorePages = () => {
    setShowAllPages(!showAllPages);
    setVisiblePages(
      showAllPages
        ? ["endpointPage", "globalConfigPage", "userManagementPage"]
        : pages.map((p) => p.id)
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="w-16 h-16 border-4 border-blue-200 border-t-[#003366] rounded-full animate-spin"></div>
      </div>
    );
  }

  if (permissions) {
    return (
      <div className="mt-12 px-4 w-full mx-auto">
        {error && (
          <ErrorNotification message={error} onClose={() => setError(null)} />
        )}
        <div className="bg-white rounded-lg shadow-lg border border-gray-200">
          <div className="p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-bold text-[#003366]">
                User Management
              </h3>
              <div className="flex gap-3">
                {isEditing ? (
                  <>
                    <button
                      onClick={() => setIsEditing(false)}
                      disabled={isSaving}
                      className={`flex items-center gap-2 px-4 py-2 bg-[#FFD700] text-[#003366] rounded hover:bg-[#FFD700]/80 
                      ${isSaving ? "opacity-50 cursor-not-allowed" : ""}`}
                    >
                      <X className="w-4 h-4" />
                      Cancel
                    </button>
                    <button
                      onClick={saveChanges}
                      disabled={isSaving}
                      className={`flex items-center gap-2 px-4 py-2 bg-[#003366] text-white rounded hover:bg-[#004488]
                      ${isSaving ? "opacity-70 cursor-not-allowed" : ""}`}
                    >
                      {isSaving ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4" />
                          Save Changes
                        </>
                      )}
                    </button>
                  </>
                ) : (
                  <button
                    onClick={toggleEditMode}
                    className="flex items-center gap-2 px-4 py-2 bg-[#003366] text-white rounded hover:bg-[#004488]"
                  >
                    <Edit2 className="w-4 h-4" />
                    Edit Users
                  </button>
                )}
              </div>
            </div>

            <div className="relative">
              {isSaving && (
                <div className="absolute inset-0 bg-white/50 z-10 flex items-center justify-center">
                  <div className="flex flex-col items-center gap-2 bg-white p-4 rounded-lg shadow-lg">
                    <Loader2 className="w-8 h-8 text-[#003366] animate-spin" />
                    <p className="text-[#003366] font-medium">
                      Saving changes...
                    </p>
                  </div>
                </div>
              )}
              <div className="overflow-x-auto">
                <table className="w-full divide-y divide-gray-200">
                  <thead className="bg-[#003366]/5">
                    <tr>
                      <th className="p-3 text-center font-semibold text-xs font-medium text-[#003366] uppercase tracking-wider relative">
                        <div className="flex items-center justify-center gap-2">
                          User
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowFilter(!showFilter);
                            }}
                            className="hover:text-[#004488] focus:outline-none"
                          >
                            <Filter
                              className={`w-4 h-4 ${filterValue ? "text-[#003366]" : "text-gray-400"}`}
                            />
                          </button>
                        </div>
                        {showFilter && (
                          <div
                            ref={filterRef}
                            className="absolute mt-2 left-0 z-20 bg-white rounded-lg shadow-lg border border-gray-200 p-2 min-w-[200px]"
                          >
                            <div className="relative">
                              <input
                                type="text"
                                value={filterValue}
                                onChange={(e) => setFilterValue(e.target.value)}
                                placeholder="Filter users..."
                                className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:border-[#003366]"
                                autoFocus
                                onClick={(e) => e.stopPropagation()}
                              />
                              {filterValue && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setFilterValue("");
                                  }}
                                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </div>
                        )}
                      </th>
                      {pages
                        .filter((page) => visiblePages.includes(page.id))
                        .map((page) => (
                          <th
                            key={page.id}
                            className="p-3 text-center text-xs font-medium text-[#003366] uppercase tracking-wider"
                            colSpan={Object.keys(pageActions).length}
                          >
                            {page.name == 'Global Config' ? 'Platform Config' : page.name}
                          </th>
                        ))}
                    </tr>
                    <tr>
                      <th></th>
                      {pages
                        .filter((page) => visiblePages.includes(page.id))
                        .map((page) =>
                          Object.keys(pageActions).map((action) => (
                            <th
                              key={`${page.id}-${action}`}
                              className="p-3 text-center text-xs font-medium text-[#003366] uppercase tracking-wider"
                            >
                              {action}
                            </th>
                          ))
                        )}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {currentUsers.map((user) => (
                      <tr key={user.userId} className="hover:bg-[#003366]/5">
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-[#003366]/10 flex items-center justify-center text-[#003366]">
                              {user.userEmail[0].toUpperCase()}
                            </div>
                            <div>
                              <div className="font-medium text-[#003366]">
                                {user.userEmail}
                              </div>
                              <div className="text-sm text-gray-500">
                                {getUserRole(user.pages)}
                              </div>
                            </div>
                          </div>
                        </td>
                        {pages
                          .filter((page) => visiblePages.includes(page.id))
                          .map((page) =>
                            Object.entries(pageActions).map(
                              ([action, level]) => {
                                const permissionLevel =
                                  level === "READ"
                                    ? 1
                                    : level === "WRITE"
                                      ? 2
                                      : level === "DELETE"
                                        ? 3
                                        : 0;

                                const isActive = isEditing
                                  ? tempPermissions[user.userId]?.[page.id] >=
                                  permissionLevel
                                  : user.pages[page.id] >= permissionLevel;

                                return (
                                  <td
                                    key={`${page.id}-${action}`}
                                    className="p-3 text-center"
                                  >
                                    <div className="flex justify-center">
                                      <PermissionCheck
                                        isActive={isActive}
                                        isUpdating={updating === user.userId}
                                        onClick={() =>
                                          isEditing &&
                                          handlePermissionChange(
                                            user.userId,
                                            page.id,
                                            isActive
                                              ? permissionLevel - 1
                                              : permissionLevel
                                          )
                                        }
                                      />
                                    </div>
                                  </td>
                                );
                              }
                            )
                          )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex justify-end items-center gap-2 mt-4">
                <div className="flex items-center gap-2 mr-auto">
                  <span className="text-sm text-gray-700">
                    Showing {indexOfFirstUser + 1} to{" "}
                    {Math.min(indexOfLastUser, filteredUsers.length)} of {filteredUsers.length}{" "}
                    entries
                  </span>
                </div>

                <button
                  onClick={handleViewMorePages}
                  className="px-3 py-1 text-sm font-medium text-[#003366] hover:text-[#004488]"
                >
                  {showAllPages ? "Show Less Pages" : "View More Pages"}
                </button>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-5 h-5 text-gray-600" />
                  </button>

                  {getPageNumbers().map((page, index) => (
                    page === '...' ? (
                      <span key={`ellipsis-${index}`} className="px-2 text-gray-600">...</span>
                    ) : (
                      <button
                        key={`page-${page}`}
                        onClick={() => setCurrentPage(page)}
                        className={`px-3 py-1 rounded-lg ${currentPage === page
                            ? "bg-[#003366] text-white"
                            : "text-gray-600 hover:bg-gray-100"
                          }`}
                      >
                        {page}
                      </button>
                    )
                  ))}

                  <button
                    onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages || totalPages === 0}
                    className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="w-5 h-5 text-gray-600" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
  return (
    <UnauthorizedPage />
  );
};

export default UserManagement;
